import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  EmojiEvents,
  TrendingUp,
  Sports,
  CheckCircle,
  Cancel,
  Schedule,
  Person,
  Assessment,
  Group
} from '@mui/icons-material';
import { SportIcon } from '../utils/sportIcons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
import { useAuth } from '../contexts/AuthContext';
import { Week, Game, PickWithGameInfo, ParticipationStatus, SeasonStanding, WeeklyStanding, StandingsSummary, Sport } from '../types';
import { weeksAPI, gamesAPI, picksAPI, standingsAPI } from '../services/api';
import { getSpreadDisplay } from '../utils/gameUtils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

interface UserStats {
  user_id: number;
  username: string;
  total_picks: number;
  correct_picks: number;
  incorrect_picks: number;
  pending_picks: number;
  confidence_score: number;
  win_percentage: number;
}

interface WeekResult {
  week: Week;
  games: Game[];
  userStats: UserStats[];
  allPicks: PickWithGameInfo[];
  participation: ParticipationStatus[];
}

const Results: React.FC = () => {
  const { user } = useAuth();
  const [weeks, setWeeks] = useState<Week[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<Week | null>(null);
  const [weekResult, setWeekResult] = useState<WeekResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [seasonStandings, setSeasonStandings] = useState<SeasonStanding[]>([]);
  const [standingsSummary, setStandingsSummary] = useState<StandingsSummary | null>(null);

  useEffect(() => {
    loadWeeks();
  }, []);

  useEffect(() => {
    if (selectedWeek) {
      loadWeekResults();
    }
  }, [selectedWeek]);

  const loadWeeks = async () => {
    try {
      setLoading(true);
      const weeksData = await weeksAPI.getAll();
      
      // Sort by year desc, then week desc (most recent first)
      const sortedWeeks = weeksData.sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.week_number - a.week_number;
      });
      
      setWeeks(sortedWeeks);
      
      // Auto-select most recent week
      if (sortedWeeks.length > 0) {
        setSelectedWeek(sortedWeeks[0]);
      }

      // Load current season standings
      await loadSeasonStandings();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load weeks');
    } finally {
      setLoading(false);
    }
  };

  const loadSeasonStandings = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const [standings, summary] = await Promise.all([
        standingsAPI.getCurrentSeasonStandings(),
        standingsAPI.getStandingsSummary(currentYear)
      ]);
      
      setSeasonStandings(standings);
      setStandingsSummary(summary);
    } catch (err: any) {
      console.error('Failed to load season standings:', err);
      // Don't set error state for standings failure, just log it
    }
  };

  const loadWeekResults = async () => {
    if (!selectedWeek) return;

    try {
      setLoading(true);
      
      // Check if picks are locked to determine what data to load
      const isPicksLocked = dayjs.utc().isAfter(dayjs.utc(selectedWeek.picks_lock_time));
      
      // Always load games and participation
      const [games, participation] = await Promise.all([
        gamesAPI.getByWeek(selectedWeek.id),
        picksAPI.getParticipation(selectedWeek.id)
      ]);

      let userStats: UserStats[] = [];
      let allPicks: PickWithGameInfo[] = [];

      // Only load picks and stats if picks are locked
      if (isPicksLocked) {
        try {
          const [statsData, picksData] = await Promise.all([
            standingsAPI.getWeeklyStandings(selectedWeek.id),
            picksAPI.getAllPicksForWeek(selectedWeek.id)
          ]);
          
          // Map weekly standings data to UserStats format
          userStats = statsData.map((stat: any) => ({
            user_id: stat.user_id,
            username: stat.username,
            total_picks: stat.total_picks,
            correct_picks: stat.correct_picks,
            incorrect_picks: stat.total_picks - stat.correct_picks, // Calculate from totals
            pending_picks: 0, // Weekly standings only show completed picks
            confidence_score: 0, // Not available in weekly standings
            win_percentage: Number(stat.win_percentage) || 0 // Convert to number for toFixed()
          }));
          
          allPicks = picksData;
        } catch (pickError) {
          // If picks/stats fail to load, continue with empty arrays
          console.log('Could not load picks/stats (picks may not be locked):', pickError);
        }
      }

      setWeekResult({
        week: selectedWeek,
        games,
        userStats,
        allPicks,
        participation
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load week results');
    } finally {
      setLoading(false);
    }
  };

  const getPicksForUserAndGame = (userId: number, gameId: number) => {
    if (!weekResult) return null;
    return weekResult.allPicks.find(pick => 
      pick.user_id === userId && pick.game_id === gameId
    );
  };

  const getGameResult = (game: Game) => {
    if (!game.is_final || game.home_score == null || game.away_score == null) {
      return 'Pending';
    }
    
    if (game.home_score > game.away_score) {
      return game.home_team;
    } else if (game.away_score > game.home_score) {
      return game.away_team;
    } else {
      return 'Tie';
    }
  };

  const formatDateTime = (dateString: string) => {
    // Parse date and convert to Eastern Time for display
    let date = dayjs(dateString);
    
    // If no timezone info, assume UTC
    if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-')) {
      date = dayjs.utc(dateString);
    }
    
    return date.tz('America/New_York').format('ddd, MMM D @ h:mm A z');
  };

  if (loading && !weekResult) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Results
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {weeks.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              No Weeks Available
            </Typography>
            <Typography variant="body1" color="textSecondary">
              No weeks have been created yet. Results and participation will appear here once weeks are set up.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Week Selection */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <FormControl fullWidth>
                <InputLabel>Select Week</InputLabel>
                <Select
                  value={selectedWeek?.id || ''}
                  onChange={(e) => {
                    const week = weeks.find(w => w.id === e.target.value);
                    setSelectedWeek(week || null);
                  }}
                  label="Select Week"
                  renderValue={(value) => {
                    const week = weeks.find(w => w.id === value);
                    if (!week) return '';
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SportIcon sport={week.sport} sx={{ fontSize: 20 }} />
                        <Typography variant="body2">
                          Week {week.week_number} - {week.year} ({week.sport === 'football' ? 'Football' : 'Basketball'})
                          {week.winner_username && ` - Winner: ${week.winner_username}`}
                        </Typography>
                      </Box>
                    );
                  }}
                >
                  {weeks.map((week) => (
                    <MenuItem key={week.id} value={week.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <SportIcon sport={week.sport} sx={{ fontSize: 20 }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            Week {week.week_number} - {week.year} ({week.sport === 'football' ? 'Football' : 'Basketball'})
                          </Typography>
                          {week.winner_username && (
                            <Typography variant="caption" color="text.secondary">
                              Winner: {week.winner_username}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>

          {weekResult && (
            <>
              {/* Week Info Header */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h5" gutterBottom>
                        Week {weekResult.week.week_number} - {weekResult.week.year}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Picks locked: {formatDateTime(weekResult.week.picks_lock_time)}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1} alignItems="center">
                      <Chip 
                        label="Completed" 
                        color="success" 
                        icon={<CheckCircle />}
                      />
                      {weekResult.week.winner_username && (
                        <Chip 
                          label={`Winner: ${weekResult.week.winner_username}`}
                          color="warning"
                          icon={<EmojiEvents />}
                        />
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Tabs for different views */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
                  <Tab label="Participation" icon={<Group />} />
                  <Tab label="Week Standings" icon={<EmojiEvents />} />
                  <Tab label="Season Standings" icon={<TrendingUp />} />
                  <Tab label="All Picks" icon={<Sports />} />
                  <Tab label="Game Results" icon={<Assessment />} />
                </Tabs>
              </Box>

              {/* Participation Tab */}
              <TabPanel value={tabValue} index={0}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Week {weekResult.week.week_number} Participation
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell align="center">Picks Made</TableCell>
                            <TableCell align="center">Total Games</TableCell>
                            <TableCell align="center">Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {weekResult.participation
                            .sort((a, b) => a.username.localeCompare(b.username))
                            .map((participant) => (
                              <TableRow 
                                key={participant.user_id}
                                sx={{ 
                                  backgroundColor: participant.username === user?.username ? 'action.hover' : 'inherit'
                                }}
                              >
                                <TableCell>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Person />
                                    {participant.username}
                                    {participant.username === user?.username && (
                                      <Chip label="You" size="small" color="primary" />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={participant.pick_count}
                                    color={participant.pick_count > 0 ? "success" : "default"}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell align="center">{participant.total_games}</TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={participant.has_complete_picks ? "Complete" : participant.pick_count > 0 ? "Partial" : "No Picks"}
                                    color={
                                      participant.has_complete_picks ? "success" : 
                                      participant.pick_count > 0 ? "warning" : "error"
                                    }
                                    size="small"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </TabPanel>

              {/* Standings Tab */}
              <TabPanel value={tabValue} index={1}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Week {weekResult.week.week_number} Standings
                    </Typography>
                    {weekResult.userStats.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Rank</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell align="center">Correct</TableCell>
                            <TableCell align="center">Total</TableCell>
                            <TableCell align="center">Win %</TableCell>
                            <TableCell align="center">Confidence Score</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {weekResult.userStats
                            .sort((a, b) => {
                              // Sort by correct picks desc, then confidence score desc
                              if (a.correct_picks !== b.correct_picks) {
                                return b.correct_picks - a.correct_picks;
                              }
                              return b.confidence_score - a.confidence_score;
                            })
                            .map((stat, index) => (
                              <TableRow 
                                key={stat.user_id}
                                sx={{ 
                                  backgroundColor: stat.username === user?.username ? 'action.hover' : 'inherit'
                                }}
                              >
                                <TableCell>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    {index === 0 && <EmojiEvents color="warning" />}
                                    #{index + 1}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Person />
                                    {stat.username}
                                    {stat.username === user?.username && (
                                      <Chip label="You" size="small" color="primary" />
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip 
                                    label={stat.correct_picks}
                                    color="success"
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell align="center">{stat.total_picks}</TableCell>
                                <TableCell align="center">
                                  {stat.win_percentage.toFixed(1)}%
                                </TableCell>
                                <TableCell align="center">
                                  <strong>{stat.confidence_score}</strong>
                                </TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    ) : (
                      <Alert severity="info">
                        <Typography variant="body1">
                          Standings will be available after picks are locked and games are completed.
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          Picks lock: {formatDateTime(weekResult.week.picks_lock_time)}
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </TabPanel>

              {/* Season Standings Tab */}
              <TabPanel value={tabValue} index={2}>
                <Box sx={{ display: 'grid', gap: 3 }}>
                  {/* Season Standings Table */}
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Season Standings {standingsSummary?.year}
                      </Typography>
                      {seasonStandings.length > 0 ? (
                        <TableContainer>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Rank</TableCell>
                                <TableCell>Player</TableCell>
                                <TableCell align="center">Correct</TableCell>
                                <TableCell align="center">Total</TableCell>
                                <TableCell align="center">Win %</TableCell>
                                <TableCell align="center">Weeks Won</TableCell>
                                <TableCell align="center">Tiebreaker Wins</TableCell>
                                <TableCell align="center">Weeks Played</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {seasonStandings.map((standing, index) => (
                                <TableRow key={standing.user_id} sx={{ 
                                  bgcolor: standing.username === user?.username ? 'action.hover' : 'inherit' 
                                }}>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {index + 1}
                                      {index === 0 && <EmojiEvents sx={{ color: 'gold' }} />}
                                      {index === 1 && <EmojiEvents sx={{ color: 'silver' }} />}
                                      {index === 2 && <EmojiEvents sx={{ color: '#CD7F32' }} />}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Person />
                                      {standing.username}
                                      {standing.username === user?.username && (
                                        <Chip label="You" size="small" color="primary" />
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="center">{standing.total_correct_picks}</TableCell>
                                  <TableCell align="center">{standing.total_picks}</TableCell>
                                  <TableCell align="center">{standing.win_percentage}%</TableCell>
                                  <TableCell align="center">
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                      {standing.weeks_won}
                                      {standing.tiebreaker_wins > 0 && (
                                        <Chip 
                                          label={`${standing.tiebreaker_wins} TB`} 
                                          size="small" 
                                          color="secondary"
                                          sx={{ fontSize: '0.7rem', height: '20px' }}
                                        />
                                      )}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="center">{standing.tiebreaker_wins}</TableCell>
                                  <TableCell align="center">{standing.weeks_participated}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      ) : (
                        <Alert severity="info">
                          No season standings available yet. Complete some weeks to see standings!
                        </Alert>
                      )}
                    </CardContent>
                  </Card>

                  {/* Weekly Winners Summary */}
                  {standingsSummary?.weekly_winners && standingsSummary.weekly_winners.length > 0 && (
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Weekly Winners
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                          {standingsSummary.weekly_winners.map((winner) => (
                            <Card key={winner.week_id} variant="outlined">
                              <CardContent sx={{ py: 2 }}>
                                <Typography variant="subtitle2" color="primary">
                                  Week {winner.week_number}
                                </Typography>
                                <Typography variant="body1" fontWeight="bold">
                                  {winner.winner_username}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {winner.correct_picks}/{winner.total_picks} correct
                                </Typography>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              </TabPanel>

              {/* All Picks Tab */}
              <TabPanel value={tabValue} index={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      All User Picks
                    </Typography>
                    {weekResult.allPicks.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Game</TableCell>
                            {weekResult.userStats.map(stat => (
                              <TableCell key={stat.user_id} align="center">
                                {stat.username}
                              </TableCell>
                            ))}
                            <TableCell align="center">Result</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {weekResult.games.map((game) => (
                            <TableRow key={game.id}>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">
                                    {game.away_team} @ {game.home_team}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    {formatDateTime(game.game_time)}
                                  </Typography>
                                  {game.is_final && game.home_score != null && game.away_score != null && (
                                    <Typography variant="caption" display="block" color="primary">
                                      Final: {game.away_team} {game.away_score} - {game.home_team} {game.home_score}
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              {weekResult.userStats.map(stat => {
                                const pick = getPicksForUserAndGame(stat.user_id, game.id);
                                return (
                                  <TableCell key={stat.user_id} align="center">
                                    {pick ? (
                                      <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                                        <Chip
                                          label={pick.selected_team}
                                          size="small"
                                          color={
                                            pick.is_correct === true ? 'success' : 
                                            pick.is_correct === false ? 'error' : 'default'
                                          }
                                          icon={
                                            pick.is_correct === true ? <CheckCircle /> :
                                            pick.is_correct === false ? <Cancel /> : <Schedule />
                                          }
                                        />
                                      </Box>
                                    ) : (
                                      <Typography variant="caption" color="textSecondary">
                                        No pick
                                      </Typography>
                                    )}
                                  </TableCell>
                                );
                              })}
                              <TableCell align="center">
                                <Chip
                                  label={getGameResult(game)}
                                  size="small"
                                  color={game.is_final ? 'primary' : 'default'}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    ) : (
                      <Alert severity="info">
                        <Typography variant="body1">
                          Individual picks will be visible after picks are locked.
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          Picks lock: {formatDateTime(weekResult.week.picks_lock_time)}
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </TabPanel>

              {/* Game Results Tab */}
              <TabPanel value={tabValue} index={4}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(300px, 1fr))' }, gap: 2 }}>
                  {weekResult.games.map((game) => (
                    <Box key={game.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: { sm: 'space-between' },
                            alignItems: { xs: 'flex-start', sm: 'flex-start' },
                            gap: { xs: 1, sm: 0 },
                            mb: 2
                          }}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="h6" gutterBottom sx={{ 
                                fontSize: { xs: '1rem', sm: '1.25rem' },
                                wordBreak: 'break-word'
                              }}>
                                {game.away_team} @ {game.home_team}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {formatDateTime(game.game_time)}
                              </Typography>
                              {game.spread && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Chip 
                                    label={getSpreadDisplay(game)}
                                    size="small" 
                                    color="info"
                                    variant="outlined"
                                    sx={{ 
                                      fontSize: '0.7rem', 
                                      fontWeight: 600,
                                      bgcolor: 'info.light',
                                      color: 'info.contrastText'
                                    }} 
                                  />
                                </Box>
                              )}
                            </Box>
                            <Box display="flex" gap={1}>
                              {game.is_spread_pick && (
                                <Chip label="Spread" size="small" color="primary" />
                              )}
                              <Chip 
                                label={game.is_final ? 'Final' : 'Pending'}
                                size="small"
                                color={game.is_final ? 'success' : 'default'}
                              />
                            </Box>
                          </Box>

                          {game.is_final && game.home_score != null && game.away_score != null ? (
                            <Box>
                              <Typography variant="h5" color="primary" gutterBottom>
                                Final Score
                              </Typography>
                              <Typography variant="h6">
                                {game.away_team}: {game.away_score}
                              </Typography>
                              <Typography variant="h6">
                                {game.home_team}: {game.home_score}
                              </Typography>
                              <Box mt={2}>
                                <Chip
                                  label={`Winner: ${getGameResult(game)}`}
                                  color="success"
                                  icon={<EmojiEvents />}
                                />
                              </Box>
                            </Box>
                          ) : (
                            <Typography variant="body1" color="textSecondary">
                              Game not yet completed
                            </Typography>
                          )}

                          {/* User Picks Section */}
                          {weekResult.userStats.length > 0 && (
                            <Box mt={3}>
                              <Typography variant="h6" gutterBottom>
                                User Picks
                              </Typography>
                              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1 }}>
                                {weekResult.userStats.map(stat => {
                                  const pick = getPicksForUserAndGame(stat.user_id, game.id);
                                  return (
                                    <Box key={stat.user_id} display="flex" alignItems="center" justifyContent="space-between" p={1} 
                                         sx={{ 
                                           backgroundColor: pick?.is_correct === true ? 'success.light' : 
                                                          pick?.is_correct === false ? 'error.light' : 'grey.100',
                                           borderRadius: 1,
                                           border: 1,
                                           borderColor: pick?.is_correct === true ? 'success.main' : 
                                                      pick?.is_correct === false ? 'error.main' : 'grey.300'
                                         }}>
                                      <Typography variant="body2" fontWeight="bold">
                                        {stat.username}
                                      </Typography>
                                      {pick ? (
                                        <Box display="flex" alignItems="center" gap={1}>
                                          <Typography variant="body2" fontWeight="bold">
                                            {pick.selected_team}
                                          </Typography>
                                          {pick.is_correct === true ? (
                                            <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />
                                          ) : pick.is_correct === false ? (
                                            <Cancel sx={{ color: 'error.main', fontSize: 16 }} />
                                          ) : (
                                            <Schedule sx={{ color: 'grey.500', fontSize: 16 }} />
                                          )}
                                        </Box>
                                      ) : (
                                        <Typography variant="body2" color="textSecondary" fontStyle="italic">
                                          No pick
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Box>
                  ))}
                </Box>
              </TabPanel>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default Results;
